<script setup>
import { computed, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";

import SidenavItem from "./SidenavItem.vue";


const HANGFIRE_URL =
    process.env.VUE_APP_HANGFIRE_URL;


const store =
    useStore();

const route =
    useRoute();


const isRTL =
    computed(() => store.state.isRTL);


// ============================================================
// MENU CONFIGURATION
// ============================================================

const menuGroups =
[
    {
        key: "sales",
        title: "Sprzedaż",
        icon: "fas fa-shopping-cart",
        color: "text-success",

        items:
        [
            

            
            {
                title: "Zamówienia",
                to: "/orders",
                icon: "fas fa-receipt",
                color: "text-success"
            },
            {
                title: "Faktury",
                to: "/invoices",
                icon: "fas fa-file-invoice",
                color: "text-info"
            },
            {
                title: "Oferty produktowe",
                to: "/productoffers",
                icon: "fas fa-tags",
                color: "text-warning"
            },
            {
                title: "Rentowność",
                to: "/inventory-profitability",
                icon: "fas fa-chart-line",
                color: "text-success"
            }
        ]
    },


    


    {
        key: "inventory",
        title: "Magazyn",
        icon: "fas fa-warehouse",
        color: "text-primary",

        items:
        [
            {
                title: "Produkty magazynowe",
                to: "/inventory",
                icon: "fas fa-boxes",
                color: "text-primary"
            },

            {
                title: "Stany magazynowe",
                to: "/inventory-stock",
                icon: "fas fa-cubes",
                color: "text-info"
            },

            {
                title: "Historia ruchów",
                to: "/inventory-activity",
                icon: "fas fa-exchange-alt",
                color: "text-secondary"
            },
        ]
    },

{
        key: "catalog",
        title: "Katalog",
        icon: "fas fa-boxes",
        color: "text-warning",

        items:
        [
            {
                title: "Produkty",
                to: "/products",
                icon: "fas fa-box",
                color: "text-primary"
            },

            {
                title: "Kategorie",
                to: "/categories",
                icon: "fas fa-folder",
                color: "text-warning"
            },

            
            {
                title: "Biblioteka mediów",
                to: "/media",
                icon: "fas fa-images",
                color: "text-info"
            }
        ]
    },
    {
        key: "production",
        title: "Produkcja",
        icon: "fas fa-industry",
        color: "text-danger",

        items:
        [
            {
                title: "Zlecenia produkcyjne",
                to: "/production/jobs",
                icon: "fas fa-clipboard-list",
                color: "text-danger"
            },

            {
                title: "Konfiguracja produkcji",
                to: "/production/configuration",
                icon: "fas fa-cogs",
                color: "text-secondary"
            },
           
           
        ]
    },


    {
        key: "tools",
        title: "Narzędzia",
        icon: "fas fa-tools",
        color: "text-info",

        items:
        [
            {
                title: "QR Code",
                to: "/qrcode",
                icon: "fas fa-qrcode",
                color: "text-dark"
            },

            {
                title: "AI",
                to: "/ai",
                icon: "fas fa-robot",
                color: "text-primary"
            },

            {
                title: "AI Chat",
                to: "/aiChat",
                icon: "fas fa-comments",
                color: "text-info"
            }, 
            {
                title: "Allegro SYNC",
                to: "/allegro/orders/sync",
                icon: "fas fa-cogs",
                color: "text-secondary"
            },
            {
                title: "Allegro TEST",
                to: "/allegro/test",
                icon: "fas fa-cogs",
                color: "text-secondary"
            },
        ]
    },


    {
        key: "system",
        title: "System",
        icon: "fas fa-server",
        color: "text-secondary",

        items:
        [
            {
                title: "Hangfire",
                to: "/hangfire",
                icon: "fas fa-tasks",
                color: "text-warning"
            },

            {
                title: "Hangfire Dashboard",
                external: true,
                href: HANGFIRE_URL,
                icon: "fas fa-external-link-alt",
                color: "text-warning"
            },

            {
                title: "Tables",
                to: "/tables",
                icon: "fas fa-table",
                color: "text-secondary"
            },

            {
                title: "Billing",
                to: "/billing",
                icon: "fas fa-credit-card",
                color: "text-success"
            }
        ]
    }
];


// ============================================================
// ACTIVE GROUP
// ============================================================

const openedGroup =
    ref(null);


// ============================================================
// ROUTE HELPERS
// ============================================================

function isRouteActive(to)
{
    if (!to)
        return false;


    return (
        route.path === to
        ||
        route.path.startsWith(
            to + "/"
        )
    );
}


function isGroupActive(group)
{
    return group.items.some(
        item =>
            !item.external
            &&
            isRouteActive(item.to)
    );
}


function syncOpenedGroup()
{
    const group =
        menuGroups.find(
            x => isGroupActive(x)
        );


    openedGroup.value =
        group?.key ?? null;
}


// ============================================================
// WATCH ROUTE
// ============================================================

watch(
    () => route.path,

    () =>
    {
        syncOpenedGroup();
    },

    {
        immediate: true
    }
);


// ============================================================
// GROUP TOGGLE
// ============================================================

function toggleGroup(groupKey)
{
    openedGroup.value =
        openedGroup.value === groupKey
            ? null
            : groupKey;
}


function isGroupOpened(groupKey)
{
    return openedGroup.value === groupKey;
}
</script>


<template>

    <div
        id="sidenav-collapse-main"
        class="collapse navbar-collapse w-auto h-auto h-100 "
    >

        <ul class="navbar-nav">


            <!-- ================================================== -->
            <!-- DASHBOARD -->
            <!-- ================================================== -->

            <li class="nav-item">

                <sidenav-item
                    to="/dashboard-default"
                    :class="
                        isRouteActive('/dashboard-default')
                            ? 'active'
                            : ''
                    "
                    :navText="
                        isRTL
                            ? 'لوحة القيادة'
                            : 'Dashboard'
                    "
                >

                    <template #icon>

                        <i
                            class="
                                ni
                                ni-tv-2
                                text-primary
                                text-sm
                                opacity-10
                            "
                        ></i>

                    </template>

                </sidenav-item>

            </li>


            <!-- ================================================== -->
            <!-- GROUPS -->
            <!-- ================================================== -->

            <li
                v-for="group in menuGroups"
                :key="group.key"
                class="nav-item menu-group"
            >

                <!-- GROUP HEADER -->

                <button
                    type="button"
                    class="nav-link group-button"
                    :class="{
                        'group-active bg-light':
                            isGroupActive(group)
                    }"
                    @click="
                        toggleGroup(group.key)
                    "
                >

                    <div
                        class="
                            icon
                            icon-shape
                            icon-sm
                            border-radius-md
                            text-center
                            me-2
                            d-flex
                            align-items-center
                            justify-content-center
                        "
                    >

                        <i
                            :class="
                            [
                                group.icon,
                                group.color,
                                'text-sm',
                                'opacity-10'
                            ]
                            "
                        ></i>

                    </div>


                    <span
                        class="
                            nav-link-text
                            ms-1
                            flex-grow-1
                            text-start
                        "
                    >
                        {{ group.title }}
                    </span>


                    <i
                        class="
                            fas
                            fa-chevron-down
                            group-chevron
                        "
                        :class="{
                            opened:
                                isGroupOpened(
                                    group.key
                                )
                        }"
                    ></i>

                </button>


                <!-- SUBMENU -->

                <transition name="menu">

                    <ul
                        v-show="
                            isGroupOpened(
                                group.key
                            )
                        "
                        class="
                            navbar-nav
                            submenu 
                        "
                    >

                        <li
                            v-for="item in group.items"
                            :key="
                                item.to
                                ??
                                item.href
                            "
                            class="nav-item"
                        >


                            <!-- INTERNAL LINK -->

                            <sidenav-item
                                v-if="!item.external"
                                :to="item.to"
                                :class="
                                    isRouteActive(item.to)
                                        ? 'bg-dark text-white'
                                        : 'bg-light'
                                "
                                :navText="item.title"
                            >

                                <template #icon>

                                    <i
                                        :class="
                                        [
                                            item.icon,
                                            item.color,
                                            'submenu-icon'
                                        ]
                                        "
                                    ></i>

                                </template>

                            </sidenav-item>


                            <!-- EXTERNAL LINK -->

                            <a
                                v-else
                                :href="item.href"
                                target="_blank"
                                rel="noopener"
                                class="nav-link external-link"
                            >

                                <div
                                    class="
                                        icon
                                        icon-shape
                                        icon-sm
                                        border-radius-md
                                        text-center
                                        me-2
                                        d-flex
                                        align-items-center
                                        justify-content-center
                                    "
                                >

                                    <i
                                        :class="
                                        [
                                            item.icon,
                                            item.color,
                                            'submenu-icon'
                                        ]
                                        "
                                    ></i>

                                </div>


                                <span
                                    class="
                                        nav-link-text
                                        ms-1
                                    "
                                >
                                    {{ item.title }}
                                </span>

                            </a>

                        </li>

                    </ul>

                </transition>

            </li>


            <!-- ================================================== -->
            <!-- ACCOUNT -->
            <!-- ================================================== -->

            <li
                class="
                    mt-4
                    mb-2
                "
            >

                <h6
                    class="
                        ps-4
                        ms-2
                        text-uppercase
                        text-xs
                        font-weight-bolder
                        opacity-6
                    "
                >
                    Konto
                </h6>

            </li>


            <li class="nav-item">

                <sidenav-item
                    to="/profile"
                    :class="
                        isRouteActive('/profile')
                            ? 'active'
                            : ''
                    "
                    :navText="
                        isRTL
                            ? 'حساب تعريفي'
                            : 'Profil'
                    "
                >

                    <template #icon>

                        <i
                            class="
                                ni
                                ni-single-02
                                text-dark
                                text-sm
                                opacity-10
                            "
                        ></i>

                    </template>

                </sidenav-item>

            </li>


        </ul>

    </div>

</template>


<style scoped>

/* ============================================================
   GROUP
   ============================================================ */

.menu-group
{
    margin-bottom: 2px;
}


.group-button
{
    width: 100%;

    border: 0;

    background: transparent;

    cursor: pointer;

    text-align: left;

    display: flex;

    align-items: center;

    color: #67748e;
}


.group-button:hover
{
    background: rgba(
        52,
        71,
        103,
        0.05
    );
}


.group-active
{
    color: #344767;

    font-weight: 600;
}


/* ============================================================
   CHEVRON
   ============================================================ */

.group-chevron
{
    margin-left: auto;

    margin-right: 8px;

    font-size: 0.65rem;

    transition:
        transform
        0.2s
        ease;
}


.group-chevron.opened
{
    transform:
        rotate(180deg);
}


/* ============================================================
   SUBMENU
   ============================================================ */

.submenu
{
    margin-top: 2px;

    margin-bottom: 6px;
}


.submenu :deep(.nav-link)
{
    padding-left: 2.6rem;

    padding-top: 0.55rem;

    padding-bottom: 0.55rem;

    font-size: 0.875rem;
}


.submenu :deep(.nav-link.active)
{
    font-weight: 600;
}


.submenu-icon
{
    font-size: 0.75rem;
}


.external-link
{
    padding-left: 2.6rem;

    padding-top: 0.55rem;

    padding-bottom: 0.55rem;

    font-size: 0.875rem;
}


/* ============================================================
   TRANSITION
   ============================================================ */

.menu-enter-active,
.menu-leave-active
{
    transition:
        all
        0.15s
        ease;
}


.menu-enter-from,
.menu-leave-to
{
    opacity: 0;

    transform:
        translateY(-4px);
}

</style>